<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import Header from './component/header.vue'

const router = useRouter()

// ── State ─────────────────────────────────────────────────────────────────────
const loading    = ref(false)
const paying     = ref(false)
const schedules  = ref({ data: [], current_page: 1, last_page: 1, total: 0 })
const summary    = ref({})
const statusTab  = ref('')          // '' = All
const search     = ref('')
const page       = ref(1)

// Payment modal
const payModal       = ref(false)
const selected       = ref(null)        // schedule being paid
const receiptFileRef = ref(null)        // hidden file input
const form           = ref({
  amount: '',
  payment_date: new Date().toISOString().slice(0, 10),
  payment_method: 'bank_transfer',
  transaction_reference: '',
  notes: '',
  file: null,
  document_name: '',
})
const formError  = ref('')

// ── API ───────────────────────────────────────────────────────────────────────
const load = async () => {
  try {
    loading.value = true
    const params = { per_page: 20, page: page.value }
    if (statusTab.value) params.status = statusTab.value
    if (search.value)    params.search = search.value

    const res = await axios.get('/procurement/payables', { params })
    schedules.value = res.data.data.schedules
    summary.value   = res.data.data.summary
  } catch (err) {
    console.error(err)
  } finally {
    loading.value = false
  }
}

watch(statusTab, () => { page.value = 1; load() })
watch(page, load)

let searchTimer = null
const onSearch = () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => { page.value = 1; load() }, 350)
}

// ── Payment modal ─────────────────────────────────────────────────────────────
const openPayModal = (schedule) => {
  selected.value  = schedule
  form.value = {
    amount: schedule.amount_remaining,
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: 'bank_transfer',
    transaction_reference: generateRef(),
    notes: '',
    file: null,
    document_name: '',
  }
  formError.value = ''
  payModal.value  = true
}

const onReceiptSelect = (e) => {
  const file = e.target.files[0]
  if (!file) return
  form.value.file = file
  form.value.document_name = form.value.document_name || file.name
}

const onReceiptDrop = (e) => {
  const file = e.dataTransfer.files[0]
  if (!file) return
  form.value.file = file
  form.value.document_name = form.value.document_name || file.name
}

const clearReceipt = () => {
  form.value.file = null
  form.value.document_name = ''
  if (receiptFileRef.value) receiptFileRef.value.value = ''
}

const closePayModal = () => {
  payModal.value = false
  selected.value = null
}

const submitPayment = async () => {
  if (!selected.value) return
  formError.value = ''
  if (!form.value.amount || form.value.amount <= 0) {
    formError.value = 'Amount must be greater than zero'
    return
  }
  if (!form.value.file) {
    formError.value = 'Payment receipt is required'
    return
  }
  try {
    paying.value = true
    const fd = new FormData()
    fd.append('schedule_id',           selected.value.id)
    fd.append('amount',                form.value.amount)
    fd.append('payment_date',          form.value.payment_date)
    fd.append('payment_method',        form.value.payment_method)
    fd.append('file',                  form.value.file)
    fd.append('document_name',         form.value.document_name || form.value.file.name)
    if (form.value.transaction_reference)
      fd.append('transaction_reference', form.value.transaction_reference)
    if (form.value.notes)
      fd.append('notes', form.value.notes)

    await axios.post(
      `/procurement/purchase-orders/${selected.value.po.id}/payments`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    closePayModal()
    load()
  } catch (err) {
    formError.value = err.response?.data?.message ?? 'Payment failed. Please try again.'
  } finally {
    paying.value = false
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const generateRef = () => {
  const date   = new Date().toISOString().slice(0, 10).replace(/-/g, '')
  const suffix = Math.random().toString(36).substring(2, 7).toUpperCase()
  return `TXN-${date}-${suffix}`
}

const fmt = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtDate = (d) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

const statusLabel = (s) => ({ overdue: 'Overdue', due: 'Due', pending: 'Upcoming', paid: 'Paid' }[s] ?? s)

const dayLabel = (item) => {
  if (!item.due_date) return item.trigger_basis === 'GOODS_RECEIPT_DATE' ? 'Awaiting GRN' : ''
  if (item.status === 'paid') return item.paid_at ? `Paid ${fmtDate(item.paid_at)}` : 'Paid'
  const diff = Math.round((new Date(item.due_date) - new Date()) / 86400000)
  if (item.status === 'overdue') return `${Math.abs(diff)} day${Math.abs(diff) !== 1 ? 's' : ''} overdue`
  if (diff === 0) return 'Due today'
  if (diff > 0)   return `In ${diff} day${diff !== 1 ? 's' : ''}`
  return `${Math.abs(diff)} day${Math.abs(diff) !== 1 ? 's' : ''} overdue`
}

const canPay = (s) => !['paid', 'cancelled'].includes(s.status)

const goToPo = (poId) => router.push({ name: 'PO Detail', params: { id: poId } })

const tabs = [
  { key: '',         label: 'All' },
  { key: 'overdue',  label: 'Overdue' },
  { key: 'due',      label: 'Due Now' },
  { key: 'pending',  label: 'Upcoming' },
  { key: 'paid',     label: 'Paid' },
]

onMounted(load)
</script>

<template>
  <div>
    <Header />

    <div class="vp-page">

      <!-- ── Page header ── -->
      <div class="ph">
        <div>
          <h5 class="ph-title">Vendor Payments</h5>
          <p class="ph-sub">Track and record all PO payment obligations</p>
        </div>
      </div>

      <!-- ── Summary chips ── -->
      <div class="summary-row" v-if="!loading">
        <div class="sum-chip sum-chip--red">
          <span class="sum-val">{{ fmt(summary.total_overdue) }}</span>
          <span class="sum-label">Overdue ({{ summary.count_overdue ?? 0 }})</span>
        </div>
        <div class="sum-chip sum-chip--yellow">
          <span class="sum-val">{{ fmt(summary.total_due) }}</span>
          <span class="sum-label">Due Now ({{ summary.count_due ?? 0 }})</span>
        </div>
        <div class="sum-chip sum-chip--blue">
          <span class="sum-val">{{ fmt(summary.total_outstanding) }}</span>
          <span class="sum-label">Outstanding ({{ summary.count_upcoming ?? 0 }} upcoming)</span>
        </div>
        <div class="sum-chip sum-chip--green">
          <span class="sum-val">{{ fmt(summary.total_paid_this_month) }}</span>
          <span class="sum-label">Paid This Month</span>
        </div>
      </div>
      <div class="summary-row" v-else>
        <div class="skeleton sum-chip" v-for="n in 4" :key="n" style="height:72px"></div>
      </div>

      <!-- ── Tab bar + search ── -->
      <div class="toolbar">
        <div class="tab-bar">
          <button
            v-for="t in tabs"
            :key="t.key"
            class="tab-btn"
            :class="{ 'tab-btn--active': statusTab === t.key }"
            @click="statusTab = t.key"
          >{{ t.label }}</button>
        </div>
        <div class="search-wrap">
          <font-awesome-icon icon="search" class="search-icon" />
          <input
            v-model="search"
            @input="onSearch"
            class="search-input"
            placeholder="Search vendor or PO#..."
          />
        </div>
      </div>

      <!-- ── Table ── -->
      <div class="table-card">
        <div v-if="loading" class="skeleton" style="height:220px;border-radius:8px"></div>

        <template v-else>
          <div v-if="schedules.data.length === 0" class="empty-state">
            <font-awesome-icon icon="check-circle" class="empty-icon" />
            <p>No payments found{{ statusTab ? ` for "${tabs.find(t=>t.key===statusTab)?.label}"` : '' }}</p>
          </div>

          <div v-else class="table-scroll">
          <table class="vp-table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>PO #</th>
                <th>Description</th>
                <th class="num">Scheduled Amount</th>
                <th class="num">Balance</th>
                <th>Due Date</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="s in schedules.data"
                :key="s.id"
                class="vp-row"
                :class="`vp-row--${s.status}`"
              >
                <td class="cell-vendor" @click="goToPo(s.po.id)" style="cursor:pointer">
                  {{ s.po?.vendor?.name ?? '—' }}
                </td>
                <td class="cell-po" @click="goToPo(s.po.id)" style="cursor:pointer">
                  {{ s.po?.po_number ?? '—' }}
                </td>
                <td class="cell-desc">{{ s.description }}</td>
                <td class="num cell-amount">{{ s.currency }} {{ fmt(s.amount) }}</td>
                <td class="num cell-balance">
                  <span v-if="s.status === 'paid'" class="balance-paid">—</span>
                  <span v-else>{{ s.currency }} {{ fmt(s.amount_remaining) }}</span>
                </td>
                <td class="cell-date">
                  <span class="date-main">{{ s.due_date ? fmtDate(s.due_date) : 'TBD' }}</span>
                  <span class="date-sub" :class="`date-sub--${s.status}`">{{ dayLabel(s) }}</span>
                </td>
                <td>
                  <span class="badge" :class="`badge--${s.status}`">{{ statusLabel(s.status) }}</span>
                </td>
                <td class="cell-action">
                  <button
                    v-if="canPay(s)"
                    class="btn-pay"
                    @click="openPayModal(s)"
                  >
                    <font-awesome-icon icon="money-bill-wave" />
                    Pay
                  </button>
                  <span v-else class="paid-check">
                    <font-awesome-icon icon="check" />
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          </div>

          <!-- Pagination -->
          <div class="pagination" v-if="schedules.last_page > 1">
            <button class="pg-btn" :disabled="page === 1" @click="page--">
              <font-awesome-icon icon="chevron-left" />
            </button>
            <span class="pg-info">Page {{ schedules.current_page }} of {{ schedules.last_page }}</span>
            <button class="pg-btn" :disabled="page >= schedules.last_page" @click="page++">
              <font-awesome-icon icon="chevron-right" />
            </button>
          </div>
        </template>
      </div>

    </div>

    <!-- ── Payment modal ── -->
    <div class="modal-backdrop" v-if="payModal" @click.self="closePayModal">
      <div class="modal-box">

        <div class="modal-head">
          <div>
            <h6 class="modal-title">Record Payment</h6>
            <p class="modal-sub" v-if="selected">
              {{ selected.po?.vendor?.name }} · {{ selected.po?.po_number }} · {{ selected.description }}
            </p>
          </div>
          <button class="modal-close" @click="closePayModal">
            <font-awesome-icon icon="times" />
          </button>
        </div>

        <div class="modal-body" v-if="selected">

          <div class="modal-info-row">
            <span>Total amount</span>
            <strong>{{ selected.currency }} {{ fmt(selected.amount) }}</strong>
          </div>
          <div class="modal-info-row">
            <span>Amount remaining</span>
            <strong>{{ selected.currency }} {{ fmt(selected.amount_remaining) }}</strong>
          </div>
          <div class="modal-info-row" v-if="selected.due_date">
            <span>Due date</span>
            <strong>{{ fmtDate(selected.due_date) }}</strong>
          </div>

          <hr class="modal-divider" />

          <div class="form-error" v-if="formError">{{ formError }}</div>

          <div class="field">
            <label class="field-label">Amount <span class="req">*</span></label>
            <div class="input-prefix">
              <span class="prefix-text">{{ selected.currency }}</span>
              <input
                v-model="form.amount"
                type="number"
                step="0.01"
                min="0.01"
                :max="selected.amount_remaining"
                class="field-input field-input--prefixed"
              />
            </div>
          </div>

          <div class="field">
            <label class="field-label">Payment Date <span class="req">*</span></label>
            <input v-model="form.payment_date" type="date" class="field-input" />
          </div>

          <div class="field">
            <label class="field-label">Payment Method</label>
            <select v-model="form.payment_method" class="field-input">
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cash">Cash</option>
              <option value="cheque">Cheque</option>
              <option value="online">Online</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div class="field">
            <label class="field-label">Transaction Reference</label>
            <div class="ref-wrap">
              <input v-model="form.transaction_reference" type="text" class="field-input ref-input" placeholder="e.g. TXN-20240901-ABC12" />
              <button type="button" class="ref-regen" title="Generate new reference" @click="form.transaction_reference = generateRef()">
                ↻
              </button>
            </div>
          </div>

          <div class="field">
            <label class="field-label">Notes</label>
            <textarea v-model="form.notes" class="field-input" rows="2" placeholder="Optional notes..."></textarea>
          </div>

          <div class="field">
            <label class="field-label">Payment Receipt <span class="req">*</span></label>
            <div
              class="drop-zone"
              :class="{ 'drop-zone--filled': form.file }"
              @click="receiptFileRef.click()"
              @dragover.prevent
              @drop.prevent="onReceiptDrop"
            >
              <template v-if="form.file">
                <font-awesome-icon icon="file-alt" class="drop-icon drop-icon--green" />
                <span class="drop-filename">{{ form.file.name }}</span>
                <button class="drop-clear" @click.stop="clearReceipt">
                  <font-awesome-icon icon="times" />
                </button>
              </template>
              <template v-else>
                <font-awesome-icon icon="upload" class="drop-icon" />
                <span>Click or drag receipt here</span>
                <small>PDF, JPG, PNG, Word, Excel — max 20 MB</small>
              </template>
            </div>
            <input
              ref="receiptFileRef"
              type="file"
              style="display:none"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
              @change="onReceiptSelect"
            />
            <input
              v-if="form.file"
              v-model="form.document_name"
              class="field-input"
              style="margin-top:6px"
              placeholder="Document name (e.g. Payment Receipt)"
            />
          </div>

          <div class="modal-note">
            <font-awesome-icon icon="info-circle" />
            A journal entry (Dr Accounts Payable / Cr Cash) will be posted automatically.
          </div>
        </div>

        <div class="modal-foot">
          <button class="btn-cancel" @click="closePayModal" :disabled="paying">Cancel</button>
          <button class="btn-confirm" @click="submitPayment" :disabled="paying || !form.file">
            <font-awesome-icon icon="spinner" spin v-if="paying" />
            <font-awesome-icon icon="check" v-else />
            {{ paying ? 'Processing...' : 'Confirm Payment' }}
          </button>
        </div>

      </div>
    </div>

  </div>
</template>

<style scoped>
.vp-page { padding: 24px; display: flex; flex-direction: column; gap: 20px; }

/* ── Page header ── */
.ph         { display: flex; align-items: flex-start; justify-content: space-between; }
.ph-title   { font-size: 18px; font-weight: 700; color: #1a2b45; margin: 0 0 4px; }
.ph-sub     { font-size: 13px; color: #7a90b0; margin: 0; }

/* ── Summary chips ── */
.summary-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.sum-chip {
  border-radius: 12px; padding: 16px 20px;
  display: flex; flex-direction: column; gap: 4px;
}
.sum-val   { font-size: 22px; font-weight: 800; line-height: 1; }
.sum-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; opacity: .75; }
.sum-chip--red    { background: rgba(224,53,53,.07);   color: #c0392b; }
.sum-chip--yellow { background: rgba(234,179,8,.09);   color: #92610a; }
.sum-chip--blue   { background: rgba(0,74,173,.07);    color: #004AAD; }
.sum-chip--green  { background: rgba(22,163,74,.08);   color: #166534; }

/* ── Toolbar ── */
.toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }

.tab-bar { display: flex; gap: 4px; background: #f0f4f8; border-radius: 8px; padding: 4px; }
.tab-btn {
  padding: 7px 16px; border: none; background: transparent;
  border-radius: 6px; font-size: 13px; font-weight: 500; color: #7a90b0;
  cursor: pointer; transition: background .12s, color .12s; white-space: nowrap;
}
.tab-btn:hover      { background: #fff; color: #374151; }
.tab-btn--active    { background: #fff; color: #004AAD; font-weight: 700; box-shadow: 0 1px 4px rgba(0,0,0,.08); }

.search-wrap  { position: relative; }
.search-icon  { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #7a90b0; font-size: 13px; }
.search-input {
  padding: 8px 12px 8px 32px; border: 1px solid #d0d9e8; border-radius: 8px;
  font-size: 13px; color: #374151; outline: none; width: 240px;
}
.search-input:focus { border-color: #004AAD; }

/* ── Table ── */
.table-card {
  background: #fff; border: 1px solid #e4eaf2; border-radius: 12px; overflow: hidden;
}
.table-scroll { overflow-x: auto; }

.vp-table { width: 100%; border-collapse: collapse; font-size: 13px; color: #374151; }
.vp-table th {
  text-align: left; font-size: 11px; font-weight: 600; text-transform: uppercase;
  letter-spacing: .04em; color: #7a90b0; padding: 12px 16px;
  border-bottom: 1px solid #e4eaf2; background: #fafbfc; white-space: nowrap;
}
.vp-table th.num, .vp-table td.num { text-align: right; }

.vp-row { transition: background .12s; }
.vp-row:hover { background: #f8fafc; }
.vp-row td { padding: 13px 16px; border-bottom: 1px solid #f0f4f8; vertical-align: middle; }
.vp-row:last-child td { border-bottom: none; }

.vp-row--overdue  td:first-child { border-left: 3px solid #e03535; }
.vp-row--due      td:first-child { border-left: 3px solid #e08c00; }
.vp-row--pending  td:first-child { border-left: 3px solid #004AAD; }
.vp-row--paid     td:first-child { border-left: 3px solid #16a34a; }

.cell-vendor  { font-weight: 600; color: #1a2b45; }
.cell-vendor:hover { color: #004AAD; }
.cell-po      { font-weight: 600; color: #004AAD; font-size: 12px; }
.cell-po:hover { text-decoration: underline; }
.cell-desc    { color: #6b7280; font-size: 12px; }
.cell-amount  { font-weight: 700; color: #1a2b45; }
.cell-balance { font-weight: 700; color: #1a2b45; }
.balance-paid { color: #9ca3af; font-weight: 400; }

.date-main { display: block; font-weight: 500; }
.date-sub  { display: block; font-size: 11px; margin-top: 2px; }
.date-sub--overdue { color: #e03535; font-weight: 600; }
.date-sub--due     { color: #b45309; font-weight: 600; }
.date-sub--pending { color: #004AAD; }
.date-sub--paid    { color: #16a34a; }

.badge {
  display: inline-block; padding: 3px 10px; border-radius: 20px;
  font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; white-space: nowrap;
}
.badge--overdue { background: rgba(224,53,53,.12);  color: #c0392b; }
.badge--due     { background: rgba(234,179,8,.15);  color: #92610a; }
.badge--pending { background: rgba(0,74,173,.1);    color: #004AAD; }
.badge--paid    { background: rgba(22,163,74,.12);  color: #166534; }

.cell-action { width: 90px; text-align: right; }
.btn-pay {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 12px; border: none; border-radius: 6px;
  background: #004AAD; color: #fff; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: background .12s;
}
.btn-pay:hover { background: #003a8c; }
.paid-check { color: #16a34a; font-size: 16px; }

.pagination {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  padding: 14px; border-top: 1px solid #f0f4f8;
}
.pg-btn {
  width: 32px; height: 32px; border: 1px solid #d0d9e8; border-radius: 6px;
  background: #fff; color: #374151; cursor: pointer; display: flex; align-items: center; justify-content: center;
}
.pg-btn:disabled { opacity: .4; cursor: default; }
.pg-info { font-size: 13px; color: #7a90b0; }

.empty-state {
  text-align: center; padding: 56px 24px; color: #9ca3af;
  display: flex; flex-direction: column; align-items: center; gap: 12px;
}
.empty-icon { font-size: 36px; color: #86efac; }
.empty-state p { margin: 0; font-size: 14px; }

/* ── Modal ── */
.modal-backdrop {
  position: fixed; inset: 0; background: rgba(0,0,0,.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 1050; padding: 16px;
}
.modal-box {
  background: #fff; border-radius: 14px; width: 100%; max-width: 480px;
  max-height: 90vh; overflow-y: auto; display: flex; flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,.2);
}
.modal-head {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
  padding: 20px 24px 16px; border-bottom: 1px solid #e4eaf2;
}
.modal-title { font-size: 16px; font-weight: 700; color: #1a2b45; margin: 0 0 3px; }
.modal-sub   { font-size: 12px; color: #7a90b0; margin: 0; }
.modal-close {
  background: none; border: none; font-size: 18px; color: #7a90b0;
  cursor: pointer; line-height: 1; flex-shrink: 0; padding: 2px;
}
.modal-close:hover { color: #1a2b45; }

.modal-body    { padding: 20px 24px; display: flex; flex-direction: column; gap: 14px; }
.modal-info-row {
  display: flex; justify-content: space-between;
  font-size: 13px; color: #374151;
}
.modal-divider { border: none; border-top: 1px solid #e4eaf2; margin: 4px 0; }
.modal-note {
  font-size: 12px; color: #7a90b0; background: #f8fafc;
  border-radius: 8px; padding: 10px 12px; display: flex; align-items: flex-start; gap: 7px;
}

/* ── Drop zone ── */
.drop-zone {
  border: 2px dashed #d0d9e8; border-radius: 8px;
  padding: 16px; cursor: pointer; text-align: center;
  display: flex; flex-direction: column; align-items: center; gap: 5px;
  font-size: 13px; color: #7a90b0;
  transition: border-color .15s, background .15s;
}
.drop-zone:hover            { border-color: #004AAD; background: #f5f9ff; }
.drop-zone--filled          { border-color: #16a34a; background: #f0fdf4; flex-direction: row; justify-content: flex-start; padding: 12px 14px; }
.drop-zone small            { font-size: 11px; color: #9ca3af; }
.drop-icon                  { font-size: 20px; color: #9ca3af; }
.drop-icon--green           { font-size: 18px; color: #16a34a; flex-shrink: 0; }
.drop-filename              { font-size: 13px; font-weight: 500; color: #1a2b45; flex: 1; text-align: left; word-break: break-all; }
.drop-clear {
  background: none; border: none; color: #9ca3af; cursor: pointer;
  font-size: 14px; padding: 2px 4px; flex-shrink: 0;
}
.drop-clear:hover { color: #e03535; }

.form-error {
  font-size: 13px; color: #e03535; background: rgba(224,53,53,.06);
  border-radius: 8px; padding: 10px 12px;
}

.field       { display: flex; flex-direction: column; gap: 5px; }
.field-label { font-size: 12px; font-weight: 600; color: #374151; }
.req         { color: #e03535; }
.field-input {
  border: 1px solid #d0d9e8; border-radius: 8px; padding: 9px 12px;
  font-size: 13px; color: #1a2b45; outline: none; transition: border-color .15s;
  width: 100%; box-sizing: border-box;
}
.field-input:focus { border-color: #004AAD; }

.input-prefix { display: flex; }
.prefix-text  {
  background: #f0f4f8; border: 1px solid #d0d9e8; border-right: none;
  border-radius: 8px 0 0 8px; padding: 9px 12px; font-size: 13px;
  font-weight: 600; color: #7a90b0; white-space: nowrap;
}
.field-input--prefixed { border-radius: 0 8px 8px 0; }

.modal-foot {
  display: flex; gap: 10px; justify-content: flex-end;
  padding: 16px 24px; border-top: 1px solid #e4eaf2;
}
.btn-cancel {
  padding: 9px 20px; border: 1px solid #d0d9e8; border-radius: 8px;
  background: #fff; font-size: 13px; font-weight: 600; color: #374151;
  cursor: pointer;
}
.btn-cancel:hover { background: #f8fafc; }
.btn-confirm {
  padding: 9px 20px; border: none; border-radius: 8px;
  background: #004AAD; color: #fff; font-size: 13px; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 6px;
  transition: background .12s;
}
.btn-confirm:hover:not(:disabled) { background: #003a8c; }
.btn-confirm:disabled { opacity: .6; cursor: not-allowed; }

/* ── Transaction ref ── */
.ref-wrap  { display: flex; gap: 6px; }
.ref-input { flex: 1; }
.ref-regen {
  flex-shrink: 0; width: 36px; height: 38px;
  border: 1px solid #d0d9e8; border-radius: 8px;
  background: #f0f4f8; color: #374151; font-size: 18px;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: background .12s, color .12s; padding: 0;
}
.ref-regen:hover { background: #e4eaf2; color: #004AAD; }

/* ── Skeleton ── */
.skeleton {
  border-radius: 8px;
  background: linear-gradient(90deg, #f0f4f8 25%, #e4eaf2 50%, #f0f4f8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
@keyframes shimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }

/* ── Responsive ── */
@media (max-width: 991px) {
  .summary-row { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 576px) {
  .vp-page     { padding: 16px; }
  .summary-row { grid-template-columns: 1fr; }
  .toolbar     { flex-direction: column; align-items: stretch; }
  .search-input { width: 100%; }
  .tab-bar     { flex-wrap: wrap; }
}
</style>
